import type { EditTarget } from '@web-studio/shared-schemas';
import { EditableContent } from '../../../edit/EditableContent';
import { pickBlockField } from '../../../lib/localized';
import { parseMenuColumns, type MenuColumn, type MenuItem } from '../../../lib/parseMenuColumns';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  isMenuColumnActive,
  isMenuItemActive,
  menuItemHasContent,
} from '../menuEditHelpers';

function EditorialItem({
  item,
  columnIndex,
  itemIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  item: MenuItem;
  columnIndex: number;
  itemIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  if (!menuItemHasContent(item)) {
    return null;
  }
  const itemActive = isMenuItemActive(
    activeEditTarget?.kind === 'menu-item' ? activeEditTarget : null,
    columnIndex,
    itemIndex,
  );
  return (
    <article
      className={['ws-menu-editorial__item', itemActive ? 'ws-edit-child--active' : '']
        .filter(Boolean)
        .join(' ')}
      {...(editable
        ? {
            'data-edit-child': 'menu-item' as const,
            'data-column-index': columnIndex,
            'data-item-index': itemIndex,
          }
        : {})}
    >
      <div className="ws-menu-editorial__layout">
        <MenuItemOrderCode code={item.code} className="ws-menu-editorial__code" />
        <div className="ws-menu-editorial__main">
          <div className="ws-menu-editorial__row">
            <MenuItemName
              item={item}
              locale={locale}
              className="ws-menu-editorial__name"
              showOrderCode={false}
            />
            <span className="ws-menu-editorial__leader" aria-hidden />
          </div>
          {item.description ? (
            <p className="ws-menu-editorial__desc">{item.description}</p>
          ) : null}
          <MenuItemDietaryTags item={item} locale={locale} />
        </div>
        <MenuItemPriceSlot price={item.price} className="ws-menu-editorial__price" />
      </div>
    </article>
  );
}

function EditorialSection({
  column,
  columnIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  column: MenuColumn;
  columnIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  const columnTitle = column.title.trim();
  const columnActive = isMenuColumnActive(activeEditTarget, columnIndex);
  return (
    <div className="ws-menu-editorial__section">
      {columnTitle ? (
        <h3
          className={[
            'ws-menu-editorial__section-title',
            columnActive ? 'ws-edit-child--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...(editable
            ? {
                'data-edit-child': 'menu-column' as const,
                'data-column-index': columnIndex,
              }
            : {})}
        >
          {column.title}
        </h3>
      ) : null}
      {column.items.map((item, itemIndex) => (
        <EditorialItem
          key={`${item.name}-${itemIndex}`}
          item={item}
          columnIndex={columnIndex}
          itemIndex={itemIndex}
          editable={editable}
          activeEditTarget={activeEditTarget}
          locale={locale}
        />
      ))}
    </div>
  );
}

/**
 * Editorial board — sparse fine-dining list with dotted price leaders.
 */
export function MenuEditorialBoard({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const columns = parseMenuColumns(menuDataRaw);
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-menu-editorial', isActive ? 'ws-block--active' : '']
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-menu-editorial__inner">
        <header className="ws-menu-editorial__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-editorial__title"
          >
            {sectionTitle}
          </EditableContent>
          <div className="ws-menu-editorial__rule" aria-hidden />
          <EditableContent
            fieldKey="sectionSubtitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-editorial__subtitle"
          >
            {sectionSubtitle}
          </EditableContent>
        </header>
        <div className="ws-menu-editorial__board">
          {columns.map((column, columnIndex) => (
            <EditorialSection
              key={`${column.title}-${columnIndex}`}
              column={column}
              columnIndex={columnIndex}
              editable={editable}
              activeEditTarget={target}
              locale={locale}
            />
          ))}
          <MenuLegalLegend columns={columns} locale={locale} />
        </div>
      </div>
    </section>
  );
}
